import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GALLERY_FOLDER_MAX_DEPTH,
  GALLERY_FOLDER_NAME_MAX_LENGTH,
  type GalleryFolder,
} from '@tailoring-crm/shared';
import { galleryFolderLabel } from '../../lib/gallery/gallery-folder-label.js';
import { useCreateGalleryFolder } from '../../lib/gallery/use-gallery.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@tailoring-crm/ui';
import { buildFolderTree, flattenFolderTree } from './gallery-folder-tree.js';

/**
 * Adding a folder. `kind` is never sent — everything a shop creates is `custom` — and the parent
 * picker never offers a folder already at {@link GALLERY_FOLDER_MAX_DEPTH}, so this dialog cannot
 * compose a request the API would have to refuse.
 */
export interface AddFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folders: readonly GalleryFolder[];
}

export function AddFolderDialog({
  open,
  onOpenChange,
  folders,
}: AddFolderDialogProps): ReactElement {
  const { t } = useTranslation();
  const create = useCreateGalleryFolder();
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const parentOptions = useMemo(
    () =>
      flattenFolderTree(buildFolderTree(folders)).filter(
        (node) => node.depth < GALLERY_FOLDER_MAX_DEPTH,
      ),
    [folders],
  );

  const reset = (): void => {
    setName('');
    setParentId('');
    setError(null);
  };

  const submit = async (): Promise<void> => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setError(null);
    try {
      await create.mutateAsync({
        name: trimmed.slice(0, GALLERY_FOLDER_NAME_MAX_LENGTH),
        ...(parentId ? { parentId } : {}),
      });
      reset();
      onOpenChange(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('error.unexpected'));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('gallery.addFolder.title')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {error ? (
            <p
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
            >
              {error}
            </p>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="gallery-folder-name">{t('gallery.addFolder.nameLabel')}</Label>
            <Input
              id="gallery-folder-name"
              value={name}
              maxLength={GALLERY_FOLDER_NAME_MAX_LENGTH}
              autoFocus
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="gallery-folder-parent">{t('gallery.addFolder.parentLabel')}</Label>
            <select
              id="gallery-folder-parent"
              value={parentId}
              onChange={(event) => setParentId(event.target.value)}
              className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
            >
              <option value="">{t('gallery.addFolder.noParent')}</option>
              {parentOptions.map(({ folder, depth }) => (
                <option key={folder.id} value={folder.id}>
                  {'—'.repeat(depth - 1)} {galleryFolderLabel(t, folder)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={!name.trim() || create.isPending}
            onClick={() => void submit()}
          >
            {t('gallery.addFolder.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
